import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { FileUploadBox } from '../FileUploadBox';
import { Input } from '@/components/ui/input';
import { useState, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

const UploadDocumentModal = ({
  selectedPart,
  open,
  onClose,
  DOCUMENT_TITLES,
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
  id,
  label,
  documentType,
  existingDocument,
  existingDocuments,
  setExistingDocuments,
  documentStates,
  setDocumentStates,
  uploadProgress,
  setUploadProgress,
  isUploading,
  setIsUploading,
  setError,
  isUploadingCertificate = false,
  editState = true,
}) => {
  const [fileName, setFileName] = useState('');
  const [authority, setAuthority] = useState('');
  const [certificateNumber, setCertificateNumber] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileUploadRef = useRef(null);

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    if (file && !isUploadingCertificate) {
      // Auto-populate fileName with the original file name (without extension)
      const nameWithoutExtension = file.name.split('.').slice(0, -1).join('.');
      setFileName(nameWithoutExtension);
    } else {
      setFileName('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!fileName.trim()) {
      setError('Please enter a file name');
      return;
    }

    try {
      // Call the upload function from FileUploadBox
      if (fileUploadRef.current && fileUploadRef.current.uploadFile) {
        await fileUploadRef.current.uploadFile(
          fileName.trim(),
          authority,
          certificateNumber,
        );
        // Reset form after successful upload
        setSelectedFile(null);
        setFileName('');
        setAuthority('');
        setCertificateNumber('');
        onClose();
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setFileName('');
    setAuthority('');
    setCertificateNumber('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="w-[300px] max-h-[95%] overflow-y-auto p-6"
        style={{ position: 'fixed', zIndex: 50 }}
        aria-describedby={undefined}
      >
        <DialogTitle className="text-gray-600 mt-[4px] mb-6 font-roboto">
          Upload File
        </DialogTitle>

        <div className="">
          <FileUploadBox
            ref={fileUploadRef}
            DOCUMENT_TITLES={DOCUMENT_TITLES}
            MAX_FILE_SIZE={MAX_FILE_SIZE}
            ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
            id={id}
            label={label}
            documentType={documentType}
            existingDocument={existingDocument}
            existingDocuments={existingDocuments}
            setExistingDocuments={setExistingDocuments}
            documentStates={documentStates}
            setDocumentStates={setDocumentStates}
            uploadProgress={uploadProgress}
            setUploadProgress={setUploadProgress}
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            setError={setError}
            onFileSelect={handleFileSelect}
            selectedFile={selectedFile}
            editState={editState}
            className="w-full"
          />

          <div className="mt-6 font-roboto">
            <Label className="text-gray-600" htmlFor="fileName">
              Enter Name of File
            </Label>
            <Input
              id="fileName"
              type="text"
              value={fileName}
              onChange={(e) => setFileName(e.target.value)}
              placeholder="Enter name"
              className="mt-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-lightPurple"
            />
          </div>

          {isUploadingCertificate && (
            <div className="mt-6 font-roboto">
              <Label className="text-gray-600" htmlFor="fileName">
                Issuing Authority
              </Label>
              <Input
                id="authority"
                type="text"
                value={authority}
                onChange={(e) => setAuthority(e.target.value)}
                placeholder="Enter issuing authority"
                className="mt-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-lightPurple"
              />
            </div>
          )}

          {isUploadingCertificate && (
            <div className="mt-6 font-roboto">
              <Label className="text-gray-600" htmlFor="fileName">
                Certificate Number
              </Label>
              <Input
                id="certificateNumber"
                type="text"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.target.value)}
                placeholder="Enter number"
                className="mt-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-slate-500 border border-gray-300 group-hover:border-lightPurple"
              />
            </div>
          )}

          <div className="mt-4 flex flex-col items-center font-roboto">
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading || !selectedFile || !fileName.trim()}
              className={`mt-3 px-4 py-2 h-12 w-36 hover:bg-darkerPurple ease-in duration-200 ${
                isUploading || !selectedFile || !fileName.trim()
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-lightPurple'
              }`}
            >
              {isUploading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Uploading...
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Upload
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadDocumentModal;
